import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { BackButton } from '@/presentation/components/BackButton';
import { AuthMethodSwitcher } from '@/presentation/components/AuthMethodSwitcher';
import { useEmployeeLookupQuery } from '../../application/timelogQueries';
import type { AttendanceAction } from '../../domain/entities/Timelog';
import { useKeypadInput } from '../hooks/useKeypadInput';

const MAX_LEN = 12;
const KEYS = ['1','2','3','4','5','6','7','8','9','⌫','0','✓'];

function accentColor(action: AttendanceAction, C: { success: string; danger: string; teal: string; blue: string }): string {
  if (action === 'entrada')         return C.blue; // was C.success — unified to C.blue (Slice B)
  if (action === 'salida')          return C.blue; // was C.danger  — unified to C.blue (Slice B)
  if (action === 'automatic')       return C.blue; // system decides — same unified blue
  if (action === 'acceso-empleado') return C.blue; // was C.teal    — unified to C.blue (Slice B)
  return C.blue;
}

export function IdInputPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { action = 'entrada' } = useParams<{ action: string }>();
  const { selectedRole } = useProfile();
  const typedAction = action as AttendanceAction;
  const accent = accentColor(typedAction, C);

  const [value, setValue] = useState('');
  const [err, setErr] = useState('');
  const [searchEnabled, setSearchEnabled] = useState(false);

  const { isLoading, data: employee, isError } = useEmployeeLookupQuery(
    value,
    selectedRole?.idAccessRole ?? null,
    searchEnabled,
  );

  // When lookup completes, navigate or show error
  useEffect(() => {
    if (!isLoading && searchEnabled && employee !== undefined) {
      if (employee) {
        const dest = typedAction === 'acceso-admin' ? `/admin-pin/${action}` : `/pin/${action}`;
        void navigate(dest, { state: { employee }, replace: false });
      } else if (isError || employee === null) {
        setErr(t('idInput.errorNotFound'));
        setSearchEnabled(false);
        setTimeout(() => { setValue(''); }, 100);
      }
    }
  }, [isLoading, searchEnabled, employee, isError, navigate, action, typedAction, t]);

  const push = useCallback((d: string) => {
    setErr('');
    setValue((p) => p.length < MAX_LEN ? p + d : p);
  }, []);
  const del = useCallback(() => { setErr(''); setValue((p) => p.slice(0, -1)); }, []);
  const submit = useCallback(() => {
    if (value.length < 4) { setErr(t('idInput.errorMinLength')); return; }
    setSearchEnabled(true);
  }, [value.length, t]);

  useKeypadInput({ onDigit: push, onDelete: del, onSubmit: submit, enabled: !isLoading });

  const isAdmin = typedAction === 'acceso-admin';
  const isEmployee = typedAction === 'acceso-empleado';
  const isAttendance = !isAdmin && !isEmployee;

  return (
    <div
      style={{
        flex: 1,
        background: T.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 30px 26px',
      }}
    >
      <div className="mi-anim-fade-in" style={{ flex: 1, width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Header row */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', marginBottom: 18, position: 'relative' }}>
          <BackButton T={T} onClick={() => { void navigate('/'); }} />
          <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, pointerEvents: 'none' }}>
            {isAdmin ? t('idInput.titleAdmin') : typedAction === 'acceso-empleado' ? t('idInput.titleEmployee') : typedAction === 'entrada' ? t('idInput.titleEntry') : t('idInput.titleExit')}
          </div>
          <div style={{ marginLeft: 'auto', padding: '5px 13px', borderRadius: 15, fontSize: 9, fontWeight: 700, letterSpacing: '.06em', background: `${accent}18`, color: accent, zIndex: 1 }}>
            ● {typedAction === 'entrada' ? t('idInput.labelEntry') : typedAction === 'salida' ? t('idInput.labelExit') : typedAction === 'acceso-empleado' ? t('idInput.labelAccess') : t('idInput.labelAdmin')}
          </div>
        </div>

        {/* Icon */}
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
          <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <circle cx="9" cy="11" r="2" />
            <path d="M14 8h4M14 12h4M5 18h14" />
          </svg>
        </div>

        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{t('idInput.heading')}</div>
        <div style={{ fontSize: 10, color: T.textMuted, textAlign: 'center', marginBottom: 18, maxWidth: 280, lineHeight: 1.6 }}>
          {isAdmin ? t('idInput.hintAdmin') : t('idInput.hintEmployee')}
        </div>

        {/* Display */}
        <div style={{ width: '100%', maxWidth: 280, height: 50, borderRadius: 10, border: `2px solid ${err ? C.danger : value ? accent : T.border}`, background: value ? (err ? C.danger : accent) + '0F' : T.surfaceBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: err ? C.danger : T.text, letterSpacing: '0.08em', fontFamily: 'Lato, sans-serif', fontVariantNumeric: 'tabular-nums', marginBottom: 10, transition: 'all .2s' }}>
          {value || <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500, letterSpacing: 0 }}>{t('idInput.placeholder')}</span>}
        </div>
        <div style={{ minHeight: 16, marginBottom: 14 }}>
          {err && <div style={{ fontSize: 10, color: C.danger, fontWeight: 600 }}>{err}</div>}
        </div>

        {/* Keypad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, width: 240, marginBottom: 14 }}>
          {KEYS.map((d, i) => (
            <button
              key={i}
              onClick={() => { if (d === '⌫') del(); else if (d === '✓') submit(); else push(d); }}
              disabled={isLoading}
              style={{
                height: 50,
                borderRadius: 12,
                border: d === '✓' ? 'none' : `1px solid ${T.border}`,
                background: d === '✓' ? (value.length >= 4 ? accent : `${accent}55`) : T.surfaceBg,
                fontFamily: 'inherit',
                fontSize: d === '✓' ? 18 : 17,
                fontWeight: 700,
                color: d === '✓' ? '#fff' : T.text,
                cursor: (d === '✓' && value.length < 4) || isLoading ? 'not-allowed' : 'pointer',
                transition: 'all .15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => { if (d !== '✓') e.currentTarget.style.background = T.rowBg; }}
              onMouseLeave={(e) => { if (d !== '✓') e.currentTarget.style.background = T.surfaceBg; }}
            >
              {d === '⌫' ? (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                  <path d="M22 3H7l-5 9 5 9h15a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
                  <line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/>
                </svg>
              ) : d === '✓' ? (isLoading ? '…' : d) : d}
            </button>
          ))}
        </div>

        {/* Alt methods for admin — kept intact, admin has its own styling */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => { void navigate(`/scan/${action}`); }} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.blue}40`, background: `${C.blue}10`, color: C.blue, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="3"/><path d="M5 20a7 7 0 0 1 14 0"/><path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z"/></svg>
              {t('methods.useFace')}
            </button>
            <button onClick={() => { void navigate(`/card-scan/${action}`); }} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.blue}40`, background: `${C.blue}10`, color: C.blue, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M16 12h.01M13 12h.01"/></svg>
              {t('methods.useCard')}
            </button>
          </div>
        )}

        {/* Alt methods for attendance (entrada/salida) — shared AuthMethodSwitcher */}
        {isAttendance && (
          <div style={{ marginTop: 14 }}>
            <AuthMethodSwitcher
              currentMethod="pin"
              action={typedAction}
              accent={accent}
              t={t}
            />
          </div>
        )}
      </div>
    </div>
  );
}
